import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquareText, Contact, Archive, Upload, Download, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/settings/Card";

const fd = (obj) => { const f = new FormData(); Object.entries(obj).forEach(([k, v]) => f.append(k, v)); return f; };

const ChatImport = () => {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ phone: "+39 ", name: "", me: "" });
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    setFile(f); setPreview(null);
    if (!f) return;
    try {
      const { data } = await api.post("/import/whatsapp-chat/preview", fd({ file: f }));
      setPreview(data);
      const guess = (f.name.match(/con (.+?)\.(txt|zip)$/i) || [])[1] || "";
      setForm((x) => ({ ...x, name: x.name || guess, me: data.senders.find((s) => s.name !== guess)?.name || data.senders[0]?.name || "" }));
    } catch (e) { toast.error(errMsg(e)); setFile(null); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/whatsapp-chat", fd({ file, ...form }));
      toast.success(`Importati ${data.imported} messaggi (${data.skipped} già presenti)`);
      qc.invalidateQueries({ queryKey: ["conversations"] });
      setFile(null); setPreview(null); setForm({ phone: "+39 ", name: "", me: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card testid="backup-chat-import-card" title="Importa chat da WhatsApp Business" desc="Una chat alla volta, dal file 'Esporta chat' del telefono" icon={MessageSquareText}>
      <ol className="text-xs text-slate-500 list-decimal pl-5 space-y-0.5 mb-4">
        <li>iPhone: apri la chat → tocca il nome → Esporta chat → Senza media. Android: chat → ⋮ → Altro → Esporta chat → Senza media.</li>
        <li>Salva il file (.zip o .txt) e caricalo qui sotto.</li>
      </ol>
      <Input data-testid="backup-chat-file-input" type="file" accept=".txt,.zip" onChange={(e) => pick(e.target.files?.[0] || null)} />
      {preview && (
        <div className="mt-4 space-y-3" data-testid="backup-chat-preview">
          <p className="text-xs text-slate-600"><b>{preview.count}</b> messaggi dal {new Date(preview.first).toLocaleDateString("it-IT")} al {new Date(preview.last).toLocaleDateString("it-IT")}</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Numero cliente</Label><Input data-testid="backup-chat-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nome cliente</Label><Input data-testid="backup-chat-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Chi sei tu nella chat?</Label>
              <Select value={form.me} onValueChange={(v) => setForm({ ...form, me: v })}>
                <SelectTrigger data-testid="backup-chat-me-select"><SelectValue /></SelectTrigger>
                <SelectContent>{preview.senders.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} ({s.count})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="backup-chat-import-button" disabled={busy || !form.me || form.phone.replace(/\D/g, "").length < 8} onClick={run} className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Upload size={15} className="mr-1.5" />{busy ? "Importazione…" : "Importa chat"}
          </Button>
        </div>
      )}
    </Card>
  );
};

const ContactsImport = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await api.post("/import/contacts", fd({ file: f }));
      toast.success(`${data.found} contatti letti: ${data.created} nuovi, ${data.updated} aggiornati`);
      qc.invalidateQueries({ queryKey: ["conversations"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card testid="backup-contacts-card" title="Importa rubrica" desc="File .vcf (vCard) esportato dai contatti del telefono" icon={Contact}>
      <p className="text-xs text-slate-500 mb-3">iPhone: app Contatti → Elenchi → tieni premuto "Tutti i contatti" → Esporta. Android: Contatti → Impostazioni → Esporta → file .vcf. I nuovi contatti finiscono tra le chat "Chiuse".</p>
      <Input data-testid="backup-contacts-file-input" type="file" accept=".vcf,text/vcard" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </Card>
  );
};

const ExportBackup = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "backup-crm.zip";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Backup scaricato");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card testid="backup-export-card" title="Backup completo del CRM" desc="Chat, messaggi, contatti (CSV per Excel), modelli, tag e valutazioni" icon={Archive}>
      <Button data-testid="backup-export-button" disabled={busy} onClick={download} className="bg-[#0F172A] hover:bg-[#1E293B]"><Download size={15} className="mr-1.5" />{busy ? "Preparazione…" : "Scarica backup (.zip)"}</Button>
    </Card>
  );
};

const WeeklyBackup = () => {
  const { data, refetch } = useQuery({ queryKey: ["backup-history"], queryFn: () => api.get("/backup/history").then((r) => r.data), refetchInterval: 10000 });
  const [busy, setBusy] = useState(false);
  const sendNow = async () => {
    setBusy(true);
    try { await api.post("/backup/send-now"); toast.success("Backup in preparazione: arriverà via email tra pochi secondi"); setTimeout(refetch, 4000); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const label = { sent: "inviato", failed: "invio fallito", pending: "in corso" };
  return (
    <Card testid="backup-weekly-card" title="Backup automatico settimanale" desc={`Ogni lunedì alle 7:00 un link al backup viene inviato a ${data?.recipient || "…"}`} icon={Mail}>
      <div className="space-y-3">
        <Button data-testid="backup-send-now-button" variant="outline" disabled={busy} onClick={sendNow}><Mail size={15} className="mr-1.5" />Invia backup ora via email</Button>
        <p className="text-[11px] text-slate-400">Il link nell'email scade dopo 7 giorni. Il destinatario è l'"Email privacy / contatto" in Azienda e logo.</p>
        <ul className="divide-y divide-slate-100 text-xs" data-testid="backup-history-list">
          {(data?.items || []).map((b) => (
            <li key={b.id || b._id} className="py-2 flex flex-wrap gap-2">
              <span className="text-slate-700 font-medium">{new Date(b.created_at).toLocaleString("it-IT")}</span>
              <span className="text-slate-400">{b.trigger === "cron" ? "automatico" : "manuale"} · {Math.round(b.size / 1024)} KB</span>
              <span className={`ml-auto font-semibold ${b.email_status === "sent" ? "text-[#15803D]" : b.email_status === "failed" ? "text-red-600" : "text-slate-500"}`}>{label[b.email_status]}{b.email_error ? `: ${b.email_error}` : ""}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

export const BackupSettings = () => (
  <div className="space-y-6">
    <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex gap-2" data-testid="backup-meta-note">
      <RefreshCw size={14} className="shrink-0 mt-0.5" />
      <span>Con la Coexistence, chat e rubrica arrivano in automatico da Meta (pulsanti in "WhatsApp Cloud API", utilizzabili una sola volta entro 24 ore dal collegamento). Il backup di WhatsApp su iCloud/Google Drive è cifrato e non è leggibile da altri programmi: per le chat più vecchie usa l'importazione qui sotto.</span>
    </div>
    <ChatImport />
    <ContactsImport />
    <ExportBackup />
    <WeeklyBackup />
  </div>
);
